import { resetComponent } from '../../style';
import { genStyleHooks } from '../../theme/internal';
import type { AliasToken, GenerateStyle } from '../../theme/internal';

export interface FilterSelectPanelToken extends AliasToken {
  componentCls: string;
  filterSelectPanelPadding: number;
  filterSelectPanelRadius: number;
  filterSelectPanelBoxShadow: string;
  filterSelectPanelBg: string;
  filterSelectPanelMenuItemPadding: number;
  filterSelectPanelMenuItemRadius: number;
  filterSelectPanelMenuItemFontSize: number;
  filterSelectPanelMenuItemFontWeight: number;
  filterSelectPanelMenuItemTransition: string;
  filterSelectPanelMenuItemHoverBg: string;
  filterSelectPanelMenuItemDisabledColor: string;
  filterSelectPanelMenuItemActiveBg: string;
  filterSelectPanelCheckboxLabelFontSize: number;
  filterSelectPanelCheckIconMarginLeft: number;
  filterSelectPanelCheckIconColor: string;
  filterSelectPanelHighlightTextColor: string;
  filterSelectPanelEmptyDescriptionFontSize: number;
  filterSelectPanelMaxHeight: number;
}

export const initFilterSelectPanelToken = (token: AliasToken): FilterSelectPanelToken => ({
  ...token,
  componentCls: '.youlify-filter-select-panel',
  filterSelectPanelPadding: 8,
  filterSelectPanelRadius: 4,
  filterSelectPanelBoxShadow: '0 4px 10px 0 rgba(17, 38, 95, 0.1)',
  filterSelectPanelBg: '#fdfdfd',
  filterSelectPanelMenuItemPadding: 8,
  filterSelectPanelMenuItemRadius: 4,
  filterSelectPanelMenuItemFontSize: 12,
  filterSelectPanelMenuItemFontWeight: 500,
  filterSelectPanelMenuItemTransition: 'all 0.3s',
  filterSelectPanelMenuItemHoverBg: '#f3f7ff',
  filterSelectPanelMenuItemDisabledColor: 'rgba(0, 0, 0, 0.25)',
  filterSelectPanelMenuItemActiveBg: '#f3f7ff',
  filterSelectPanelCheckboxLabelFontSize: 12,
  filterSelectPanelCheckIconMarginLeft: 8,
  filterSelectPanelCheckIconColor: '#2b6ff6',
  filterSelectPanelHighlightTextColor: '#2b6ff6',
  filterSelectPanelEmptyDescriptionFontSize: 12,
  filterSelectPanelMaxHeight: 300,
});

const genFilterSelectPanelStyle: GenerateStyle<FilterSelectPanelToken> = (token) => {
  const {
    componentCls,
    filterSelectPanelPadding,
    filterSelectPanelRadius,
    filterSelectPanelBoxShadow,
    filterSelectPanelBg,
    filterSelectPanelMenuItemPadding,
    filterSelectPanelMenuItemRadius,
    filterSelectPanelMenuItemFontSize,
    filterSelectPanelMenuItemFontWeight,
    filterSelectPanelMenuItemTransition,
    filterSelectPanelMenuItemHoverBg,
    filterSelectPanelMenuItemDisabledColor,
    filterSelectPanelMenuItemActiveBg,
    filterSelectPanelCheckboxLabelFontSize,
    filterSelectPanelCheckIconMarginLeft,
    filterSelectPanelCheckIconColor,
    filterSelectPanelHighlightTextColor,
    filterSelectPanelEmptyDescriptionFontSize,
    filterSelectPanelMaxHeight,
  } = token;

  return {
    [componentCls]: {
      ...resetComponent(token),
      padding: filterSelectPanelPadding,
      borderRadius: filterSelectPanelRadius,
      boxShadow: filterSelectPanelBoxShadow,
      background: filterSelectPanelBg,
      maxHeight: filterSelectPanelMaxHeight,
      overflowY: 'auto',
      '.menu-item': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: filterSelectPanelMenuItemPadding,
        borderRadius: filterSelectPanelMenuItemRadius,
        fontSize: filterSelectPanelMenuItemFontSize,
        fontWeight: filterSelectPanelMenuItemFontWeight,
        transition: filterSelectPanelMenuItemTransition,
        cursor: 'pointer',
        '&:hover': {
          background: filterSelectPanelMenuItemHoverBg,
        },
        '&.menu-item-disabled': {
          color: filterSelectPanelMenuItemDisabledColor,
          cursor: 'not-allowed',
        },
        '&.menu-item-active': {
          background: filterSelectPanelMenuItemActiveBg,
        },
        '.youlify-checkbox-label': {
          fontSize: filterSelectPanelCheckboxLabelFontSize,
        },
        '.check-icon': {
          marginLeft: filterSelectPanelCheckIconMarginLeft,
          color: filterSelectPanelCheckIconColor,
        },
      },
      '.highlight-text': {
        color: filterSelectPanelHighlightTextColor,
      },
      '.youlify-empty-description': {
        fontSize: filterSelectPanelEmptyDescriptionFontSize,
      },
    },
  };
};

export const useSelectPanelStyle = genStyleHooks(
  ['Input', 'Component'],
  (token) => [genFilterSelectPanelStyle(initFilterSelectPanelToken(token))],
  undefined,
  { resetFont: false },
);
